import { useState, useEffect } from "react";
import {
  LETTER_WIDTH_PX,
  LETTER_HEIGHT_PX,
  normalizePortraitDimensions,
} from "../constants/pageDimensions";
import { GUTTER_SIZE, CELL_MIN_SIZE } from "../constants/theme";
import { withRect } from "../utils/panelGeometry";

/**
 * Custom hook to handle edge and corner dragging for cell resizing
 * @param {Array} cells - Current cells state
 * @param {Function} setCells - Function to update cells state
 * @param {Object} containerSize - Container dimensions {width, height}
 * @param {Object} layout - Layout object with page_width and page_height
 * @param {Function} onGestureStart - Opens an undo gesture when a drag begins
 * @param {number} gutterSizeOverride - Current gutter width
 * @param {Function} onGestureEnd - Closes the undo gesture when the drag ends
 * @param {Function} getScale - Current canvas zoom, to convert screen px to page px
 * @returns {Object} - { draggingEdge, startDragEdge }
 */
export const useEdgeDragging = (
  cells,
  setCells,
  containerSize,
  layout,
  onGestureStart,
  gutterSizeOverride,
  onGestureEnd,
  getScale
) => {
  const GUTTER = gutterSizeOverride ?? GUTTER_SIZE;
  const [draggingEdge, setDraggingEdge] = useState(null);

  // Start dragging an edge
  const startDragEdge = (cellId, edge, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onGestureStart) {
      onGestureStart();
    }

    const scale = getScale ? getScale() : 1;
    setDraggingEdge({
      cellId,
      edge,
      startX: e.clientX,
      startY: e.clientY,
      scale: scale > 0 ? scale : 1,
      originalCells: [...cells],
    });
  };

  // Handle edge dragging
  useEffect(() => {
    if (!draggingEdge) return;

    const handleMouseMove = (e) => {
      let deltaX = (e.clientX - draggingEdge.startX) / draggingEdge.scale;
      let deltaY = (e.clientY - draggingEdge.startY) / draggingEdge.scale;

      const newCells = draggingEdge.originalCells.map(c => ({ ...c }));
      const cellIndex = newCells.findIndex((c) => c.id === draggingEdge.cellId);
      const originalCell = draggingEdge.originalCells[cellIndex];

      const edge = draggingEdge.edge;
      const { width: layoutWidth, height: layoutHeight } = normalizePortraitDimensions(
        layout?.page_width,
        layout?.page_height
      );
      const containerWidth = containerSize.width || layoutWidth || LETTER_WIDTH_PX;
      const containerHeight = containerSize.height || layoutHeight || LETTER_HEIGHT_PX;

      // Corner drags are handled by processing X and Y edges independently
      // No special diagonal neighbor logic needed - the edge handlers and
      // final overlap check handle all cases

      if (edge === "right" || edge === "se" || edge === "ne") {
        // Find cells to the right - any cell whose left edge is to the right of current cell's right edge
        // AND overlaps vertically
        const cellsToRight = draggingEdge.originalCells.filter(
          (c) => c.id !== originalCell.id &&
          c.x >= originalCell.x + originalCell.width && // To the right
          c.x <= originalCell.x + originalCell.width + GUTTER + 10 && // Within reasonable proximity
          c.y < originalCell.y + originalCell.height && // Vertical overlap
          c.y + c.height > originalCell.y
        );

        let constrainedDelta = deltaX;

        // Constraint 1: Current cell minimum size
        constrainedDelta = Math.max(constrainedDelta, CELL_MIN_SIZE - originalCell.width);

        // Constraint 2: Container boundary (if at edge)
        if (cellsToRight.length === 0) {
          constrainedDelta = Math.min(constrainedDelta, containerWidth - (originalCell.x + originalCell.width));
        }

        // Constraint 3: When GROWING (positive delta), check gutter and adjacent cells
        if (constrainedDelta > 0) {
          cellsToRight.forEach((rightCell) => {
            const currentGap = rightCell.x - (originalCell.x + originalCell.width);
            const newGap = currentGap - constrainedDelta; // Gap shrinks when we grow

            // A neighbour sharing this gutter gets pushed (below); a distant
            // one just stops the edge before the gap gets narrower than GUTTER
            if (currentGap > GUTTER + 5 && newGap < GUTTER) {
              constrainedDelta = Math.min(constrainedDelta, currentGap - GUTTER);
            }

            // Prevent right cell from shrinking below minimum (only if we're moving it)
            if (currentGap <= GUTTER + 5) {
              const maxShrink = rightCell.width - CELL_MIN_SIZE;
              constrainedDelta = Math.min(constrainedDelta, maxShrink);
            }
          });
        }
        
        // Apply constrained delta
        newCells[cellIndex].width = originalCell.width + constrainedDelta;
        
        // Update adjacent cells - only when growing AND they share the gutter
        if (constrainedDelta > 0) {
          cellsToRight.forEach((rightCell) => {
            const currentGap = rightCell.x - (originalCell.x + originalCell.width);
            if (currentGap <= GUTTER + 5) { // Only move if currently sharing gutter
              const rightIndex = newCells.findIndex((p) => p.id === rightCell.id);
              newCells[rightIndex].x = rightCell.x + constrainedDelta;
              newCells[rightIndex].width = rightCell.width - constrainedDelta;
            }
          });
        }
        // When shrinking (negative delta), neighbors don't move - gutter just grows
      }

      if (edge === "left" || edge === "sw" || edge === "nw") {
        // Find cells to the left - any panel whose right edge is to the left of current panel's left edge
        // AND overlaps vertically
        const cellsToLeft = draggingEdge.originalCells.filter(
          (p) => p.id !== originalCell.id &&
          p.x + p.width <= originalCell.x && // To the left
          p.x + p.width >= originalCell.x - GUTTER - 10 && // Within reasonable proximity
          p.y < originalCell.y + originalCell.height && // Vertical overlap
          p.y + p.height > originalCell.y
        );
        
        let constrainedDelta = deltaX;
        
        // Constraint 1: Current panel minimum size
        constrainedDelta = Math.min(constrainedDelta, originalCell.width - CELL_MIN_SIZE);
        
        // Constraint 2: Container boundary (if at edge)
        if (cellsToLeft.length === 0) {
          constrainedDelta = Math.max(constrainedDelta, -originalCell.x);
        }
        
        // Constraint 3: When GROWING left (negative delta), check gutter and adjacent cells
        if (constrainedDelta < 0) {
          cellsToLeft.forEach((leftCell) => {
            const currentGap = originalCell.x - (leftCell.x + leftCell.width);
            const newGap = currentGap + constrainedDelta; // Gap shrinks when we grow left
            
            // A neighbour sharing this gutter gets pushed (below); a distant
            // one just stops the edge before the gap gets narrower than GUTTER
            if (currentGap > GUTTER + 5 && newGap < GUTTER) {
              constrainedDelta = Math.max(constrainedDelta, GUTTER - currentGap);
            }
            
            // Prevent left panel from shrinking below minimum (only if we're moving it)
            if (currentGap <= GUTTER + 5) {
              const maxShrink = leftCell.width - CELL_MIN_SIZE;
              constrainedDelta = Math.max(constrainedDelta, -maxShrink);
            }
          });
        }
        
        // Apply constrained delta
        newCells[cellIndex].x = originalCell.x + constrainedDelta;
        newCells[cellIndex].width = originalCell.width - constrainedDelta;
        
        // Update adjacent cells - only when growing left (negative delta) AND they share the gutter
        if (constrainedDelta < 0) {
          cellsToLeft.forEach((leftCell) => {
            const currentGap = originalCell.x - (leftCell.x + leftCell.width);
            if (currentGap <= GUTTER + 5) { // Only resize if currently sharing gutter
              const leftIndex = newCells.findIndex((p) => p.id === leftCell.id);
              newCells[leftIndex].width = leftCell.width + constrainedDelta;
            }
          });
        }
        // When shrinking from left (positive delta), neighbors don't move - gutter just grows
      }

      if (edge === "bottom" || edge === "se" || edge === "sw") {
        // Find cells below - any panel whose top edge is below current panel's bottom edge
        // AND overlaps horizontally
        const cellsBelow = draggingEdge.originalCells.filter(
          (p) => p.id !== originalCell.id &&
          p.y >= originalCell.y + originalCell.height && // Below
          p.y <= originalCell.y + originalCell.height + GUTTER + 10 && // Within reasonable proximity
          p.x < originalCell.x + originalCell.width && // Horizontal overlap
          p.x + p.width > originalCell.x
        );
        
        let constrainedDelta = deltaY;
        
        // Constraint 1: Current panel minimum size
        constrainedDelta = Math.max(constrainedDelta, CELL_MIN_SIZE - originalCell.height);
        
        // Constraint 2: Container boundary (if at edge)
        if (cellsBelow.length === 0) {
          constrainedDelta = Math.min(constrainedDelta, containerHeight - (originalCell.y + originalCell.height));
        }
        
        // Constraint 3: When GROWING (positive delta), check gutter and adjacent cells
        if (constrainedDelta > 0) {
          cellsBelow.forEach((belowCell) => {
            const currentGap = belowCell.y - (originalCell.y + originalCell.height);
            const newGap = currentGap - constrainedDelta; // Gap shrinks when we grow
            
            // A neighbour sharing this gutter gets pushed (below); a distant
            // one just stops the edge before the gap gets narrower than GUTTER
            if (currentGap > GUTTER + 5 && newGap < GUTTER) {
              constrainedDelta = Math.min(constrainedDelta, currentGap - GUTTER);
            }
            
            // Prevent below panel from shrinking below minimum (only if we're moving it)
            if (currentGap <= GUTTER + 5) {
              const maxShrink = belowCell.height - CELL_MIN_SIZE;
              constrainedDelta = Math.min(constrainedDelta, maxShrink);
            }
          });
        }
        
        // Apply constrained delta
        newCells[cellIndex].height = originalCell.height + constrainedDelta;
        
        // Update adjacent cells - only when growing AND they share the gutter
        if (constrainedDelta > 0) {
          cellsBelow.forEach((belowCell) => {
            const currentGap = belowCell.y - (originalCell.y + originalCell.height);
            if (currentGap <= GUTTER + 5) { // Only move if currently sharing gutter
              const belowIndex = newCells.findIndex((p) => p.id === belowCell.id);
              newCells[belowIndex].y = belowCell.y + constrainedDelta;
              newCells[belowIndex].height = belowCell.height - constrainedDelta;
            }
          });
        }
        // When shrinking (negative delta), neighbors don't move - gutter just grows
      }

      if (edge === "top" || edge === "ne" || edge === "nw") {
        // Find cells above - any panel whose bottom edge is above current panel's top edge
        // AND overlaps horizontally
        const cellsAbove = draggingEdge.originalCells.filter(
          (p) => p.id !== originalCell.id &&
          p.y + p.height <= originalCell.y && // Above
          p.y + p.height >= originalCell.y - GUTTER - 10 && // Within reasonable proximity
          p.x < originalCell.x + originalCell.width && // Horizontal overlap
          p.x + p.width > originalCell.x
        );
        
        let constrainedDelta = deltaY;
        
        // Constraint 1: Current panel minimum size
        constrainedDelta = Math.min(constrainedDelta, originalCell.height - CELL_MIN_SIZE);
        
        // Constraint 2: Container boundary (if at edge)
        if (cellsAbove.length === 0) {
          constrainedDelta = Math.max(constrainedDelta, -originalCell.y);
        }
        
        // Constraint 3: When GROWING up (negative delta), check gutter and adjacent cells
        if (constrainedDelta < 0) {
          cellsAbove.forEach((aboveCell) => {
            const currentGap = originalCell.y - (aboveCell.y + aboveCell.height);
            const newGap = currentGap + constrainedDelta; // Gap shrinks when we grow up
            
            // A neighbour sharing this gutter gets pushed (below); a distant
            // one just stops the edge before the gap gets narrower than GUTTER
            if (currentGap > GUTTER + 5 && newGap < GUTTER) {
              constrainedDelta = Math.max(constrainedDelta, GUTTER - currentGap);
            }
            
            // Prevent above panel from shrinking below minimum (only if we're moving it)
            if (currentGap <= GUTTER + 5) {
              const maxShrink = aboveCell.height - CELL_MIN_SIZE;
              constrainedDelta = Math.max(constrainedDelta, -maxShrink);
            }
          });
        }
        
        // Apply constrained delta
        newCells[cellIndex].y = originalCell.y + constrainedDelta;
        newCells[cellIndex].height = originalCell.height - constrainedDelta;
        
        // Update adjacent cells - only when growing up (negative delta) AND they share the gutter
        if (constrainedDelta < 0) {
          cellsAbove.forEach((aboveCell) => {
            const currentGap = originalCell.y - (aboveCell.y + aboveCell.height);
            if (currentGap <= GUTTER + 5) { // Only resize if currently sharing gutter
              const aboveIndex = newCells.findIndex((p) => p.id === aboveCell.id);
              newCells[aboveIndex].height = aboveCell.height + constrainedDelta;
            }
          });
        }
        // When shrinking from top (positive delta), neighbors don't move - gutter just grows
      }

      // Final validation: prevent any overlaps with ALL other cells
      // This catches diagonal cases that edge-specific logic might miss
      let hasOverlap = false;
      for (let i = 0; i < newCells.length; i++) {
        for (let j = i + 1; j < newCells.length; j++) {
          const p1 = newCells[i];
          const p2 = newCells[j];
          // Halves of an angled cut share a bounding box by design
          if (p1.polygon && p2.polygon) continue;
          
          // Check if cells overlap (accounting for required gutter)
          const horizontalOverlap = 
            p1.x < p2.x + p2.width + GUTTER &&
            p1.x + p1.width + GUTTER > p2.x;
          
          const verticalOverlap = 
            p1.y < p2.y + p2.height + GUTTER &&
            p1.y + p1.height + GUTTER > p2.y;
          
          if (horizontalOverlap && verticalOverlap) {
            hasOverlap = true;
            break;
          }
        }
        if (hasOverlap) break;
      }
      
      // Only apply changes if no overlaps detected. Angled panels that got
      // pushed rescale their outline into their new box.
      if (!hasOverlap) {
        setCells(newCells.map((cell, i) => {
          const original = draggingEdge.originalCells[i];
          return cell.polygon ? withRect(original, {
            x: cell.x, y: cell.y, width: cell.width, height: cell.height,
          }) : cell;
        }));
      }
    };

    const handleMouseUp = () => {
      setDraggingEdge(null);
      if (onGestureEnd) onGestureEnd();
    };

    document.addEventListener("pointermove", handleMouseMove);
    document.addEventListener("pointerup", handleMouseUp);
    document.addEventListener("pointercancel", handleMouseUp);

    return () => {
      document.removeEventListener("pointermove", handleMouseMove);
      document.removeEventListener("pointerup", handleMouseUp);
      document.removeEventListener("pointercancel", handleMouseUp);
    };
  }, [draggingEdge, cells, setCells, containerSize, layout, onGestureEnd, GUTTER]);

  return { draggingEdge, startDragEdge };
};

